/* eslint-disable no-unused-vars */
import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  auth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "../utils/firebase";
import "./Login.css";
import googleLogo from "./GoogleG.png";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { rtdb } from "../utils/firebase";
import { useAuth } from "./AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { TokenContext } from "./tokenContext";
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
} from "firebase/auth";
import { ref, set, onDisconnect, serverTimestamp } from "firebase/database";

function resolveMfaFactor(resv) {
  if (!resv) return null;
  const hints = resv.hints || [];
  if (hints.some((h) => h.factorId === "totp")) return "totp";
  if (hints.some((h) => !!h.phoneNumber)) return "sms";
  return "other";
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [smsCode, setSmsCode] = useState("");
  const [resolver, setResolver] = useState(null);
  const [totpCodeLogin, setTotpCodeLogin] = useState("");
  const [loginVerificationId, setLoginVerificationId] = useState(null);
  const navigate = useNavigate();
  const { setUserRole } = useAuth();
  const { purchaseIntent, setPurchaseIntent, planIntent, setPlanIntent } = useContext(TokenContext);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [verifyingMfa, setVerifyingMfa] = useState(false);
  const mfaInputRef = useRef(null);
  const loginLock = useRef(false);
  const googleLock = useRef(false);
  const verifyLock = useRef(false);

  const mfaFactor = resolveMfaFactor(resolver);
  const mfaFieldReady =
    mfaFactor === "totp" ||
    mfaFactor === "other" ||
    (mfaFactor === "sms" && !!loginVerificationId);
  const showLoginChoices = !mfaFactor && !googlePending;

  useEffect(() => {
    if (mfaFieldReady && !verifyingMfa) {
      mfaInputRef.current?.focus();
    }
  }, [mfaFieldReady, verifyingMfa, error]);

  function initRecaptchaLogin() {
    if (!window.recaptchaVerifierLogin) {
      window.recaptchaVerifierLogin = new RecaptchaVerifier(
        auth,
        "recaptcha-login-container",
        { size: "invisible" }
      );
    }
  }

  async function ensurePlanState(uidOrRef) {
    try {
      const ref = typeof uidOrRef === "string" ? doc(db, "users", uidOrRef) : uidOrRef;
      const snap = await getDoc(ref);
      const u = snap.data() || {};
      const exp = u?.planInfo?.expiresAt?.toDate?.();
      if (u?.planInfo?.status === "active" && exp && exp <= new Date()) {
        await updateDoc(ref, {
          userType: "token",
          "planInfo.status": "expired",
        });
      }
    } catch (e) {
      console.warn("ensurePlanState() skipped:", e);
    }
  }

  async function checkoutForPlanIntent(user, planKey) {
    try {
      const stripe = await loadStripe(process.env.REACT_APP_STP_TST);
      const idToken = await user.getIdToken();
      const res = await fetch("https://fluency.ispeakwell.ca/api/create-plan-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          planKey,
          frontendOrigin: window.location.origin,
        }),
      });
      if (!res.ok) throw new Error(`create-plan-checkout-session failed: ${res.status}`);
      const { sessionId } = await res.json();
      await stripe.redirectToCheckout({ sessionId });
    } finally {
      setPlanIntent(null);
    }
  }

  async function startMfaFlow(resv) {
    try {
      initRecaptchaLogin();
      const hints = resv.hints || [];
      const hasTotp = hints.some(h => h.factorId === "totp");
      // Prefer TOTP if present (no SMS needed)
      if (hasTotp) {
        setError("");
        setResolver(resv);
        return;
      }
      // Otherwise, if we have a phone factor, do SMS
      const hint = hints.find(h => !!h.phoneNumber);
      if (hint?.phoneNumber) {
        setError("");
        setResolver(resv);
        const phoneInfoOptions = { multiFactorHint: hint, session: resv.session };
        const provider = new PhoneAuthProvider(auth);
        const vId = await provider.verifyPhoneNumber(
          phoneInfoOptions,
          window.recaptchaVerifierLogin
        );
        setLoginVerificationId(vId);
        return;
      }
      setError("");
      setResolver(resv);
    } catch (sendErr) {
      console.error("Failed to send MFA SMS:", sendErr);
      setResolver(null);
      setLoginVerificationId(null);
      setError("Could not send verification code. Please try again.");
    }
  }

  auth.onAuthStateChanged(user => {
    if (user) {
      const userStatusRef = ref(rtdb, `/status/${user.uid}`);
      // Online
      set(userStatusRef, {
        state: "online",
        last_changed: serverTimestamp(),
      });
      // Offline when browser closes or network drops
      onDisconnect(userStatusRef).set({
        state: "offline",
        last_changed: serverTimestamp(),
      });
    }
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (auth.currentUser) {
        const statusRef = ref(rtdb, `/status/${auth.currentUser.uid}`);
        set(statusRef, {
          state: "offline",
          last_changed: serverTimestamp(),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const focusMfaInput = () => {
    requestAnimationFrame(() => {
      mfaInputRef.current?.focus();
    });
  };

  const handleVerifySms = async (event) => {
    event.preventDefault();
    if (verifyLock.current) return;
    if (!resolver || !loginVerificationId || !smsCode) {
      setError("Enter the code sent to your phone.");
      focusMfaInput();
      return;
    }
    verifyLock.current = true;
    setVerifyingMfa(true);
    setError("");
    try {
      const cred = PhoneAuthProvider.credential(loginVerificationId, smsCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      const userCredential = await resolver.resolveSignIn(assertion, { rememberDevice });
      const user = userCredential.user;
      const userRole = await fetchUserRole(user.uid);
      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);
      await ensurePlanState(user.uid);
      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }
      const shouldGoToPurchase = purchaseIntent > 0;
      const redirectPath = shouldGoToPurchase ? "/purchase" : "/voicebot";
      if (!shouldGoToPurchase) setPurchaseIntent(0);
      navigate(redirectPath);
    } catch (err) {
      console.error("Error verifying SMS code:", err);
      setSmsCode("");
      setError(
        err?.code === "auth/invalid-verification-code"
          ? "That code wasn't accepted. Check the code and try again."
          : "Invalid code. Please try again."
      );
      focusMfaInput();
    } finally {
      verifyLock.current = false;
      setVerifyingMfa(false);
    }
  };

  const handleVerifyTotp = async (event) => {
    event.preventDefault();
    if (verifyLock.current) return;

    if (!resolver) {
      setError("Multi-factor resolver missing. Try signing in again.");
      return;
    }

    const code = String(totpCodeLogin || "")
      .replace(/\s|-/g, "")
      .trim();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter all 6 digits from your authenticator app.");
      focusMfaInput();
      return;
    }
    verifyLock.current = true;
    setVerifyingMfa(true);
    setError("");
    try {
      // find the TOTP factor in the resolver.hints list
      const totpHint = resolver.hints.find(h => h.factorId === "totp");
      if (!totpHint) {
        setError("No authenticator app factor found for this account.");
        return;
      }
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,  // enrollmentId of the TOTP factor
        code
      );
      const userCredential = await resolver.resolveSignIn(assertion, { rememberDevice });
      const user = userCredential.user;
      const userRole = await fetchUserRole(user.uid);
      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);
      await ensurePlanState(user.uid);
      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }
      const shouldGoToPurchase = purchaseIntent > 0;
      const redirectPath = shouldGoToPurchase ? "/purchase" : "/voicebot";
      if (!shouldGoToPurchase) setPurchaseIntent(0);
      navigate(redirectPath);
    } catch (err) {
      console.error("Error verifying TOTP code:", err);
      setTotpCodeLogin("");
      setError(
        err?.code === "auth/invalid-verification-code"
          ? "That code wasn't accepted. Check the code and try again."
          : "Could not verify the authenticator code. Try again."
      );
      focusMfaInput();
    } finally {
      verifyLock.current = false;
      setVerifyingMfa(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loginLock.current || googleLock.current) return;
    loginLock.current = true;
    setLoginPending(true);
    setError("");
    setInfo("");
    setUnverifiedUser(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        const actionCodeSettings = {
          url: "https://ispeakwell.ca/login",   // where users land after verifying
          handleCodeInApp: false,
        };
        await sendEmailVerification(user, actionCodeSettings);
        setUnverifiedUser(user);
        setInfo("Email not verified. A verification email has been sent. Please verify before continuing.");
        return;
      }

      const userRole = await fetchUserRole(user.uid);
      setUserRole(userRole);
      sessionStorage.setItem("userRole", userRole);
      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }
      const shouldGoToPurchase = purchaseIntent > 0;
      const redirectPath = shouldGoToPurchase ? "/purchase" : "/voicebot";
      if (!shouldGoToPurchase) setPurchaseIntent(0);
      navigate(redirectPath);
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const resv = getMultiFactorResolver(auth, err); // ✅ correct for password flow
        await startMfaFlow(resv);
      } else {
        console.error("Error logging in:", err);
        setError("An error occurred while trying to log in. Please try again.");
      }
    } finally {
      loginLock.current = false;
      setLoginPending(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const target = unverifiedUser || auth.currentUser;
      if (!target) {
        setError("Please log in again to resend verification.");
        return;
      }

      const actionCodeSettings = {
        url: "https://ispeakwell.ca/login",
        handleCodeInApp: false,
      };

      await sendEmailVerification(target, actionCodeSettings);
      setInfo("Verification email resent. Please check your inbox.");
    } catch (err) {
      console.error("Failed to resend verification email:", err);
      setError("Could not resend verification email.");
    }
  };

  const handleGoogleSignIn = async () => {
    if (googleLock.current || loginLock.current) return;
    googleLock.current = true;
    setGooglePending(true);
    setError("");
    setInfo("");
    setUnverifiedUser(null);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (!userData.firstName || !userData.lastName) {
          let [firstName, lastName] = user.displayName ? user.displayName.split(" ") : ["", ""];
          lastName = lastName || "";
          await setDoc(userDocRef, { ...userData, firstName, lastName }, { merge: true });
        }
      } else {
        let [firstName, lastName] = user.displayName ? user.displayName.split(" ") : ["", ""];
        lastName = lastName || "";
        await setDoc(userDocRef, {
          email: user.email,
          firstName,
          lastName,
          role: "user",
          tokens: 2,
          desiredPurchase: 0,
          paying: false,
        });
      }

      const userRole = await fetchUserRole(user.uid);
      setUserRole(userRole || "user");
      await ensurePlanState(user.uid);

      if (planIntent) {
        await checkoutForPlanIntent(user, planIntent);
        return;
      }
      const shouldGoToPurchase = purchaseIntent > 0;
      const redirectPath = shouldGoToPurchase ? "/purchase" : "/voicebot";
      if (!shouldGoToPurchase) setPurchaseIntent(0);
      navigate(redirectPath);
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        const resv = getMultiFactorResolver(auth, err);
        await startMfaFlow(resv);
      } else if (
        err.code === "auth/popup-closed-by-user" ||
        err.code === "auth/cancelled-popup-request"
      ) {
        setError("");
      } else {
        console.error("Error logging in with Google:", err);
        setError("There was an issue with Google sign-in. Please try again.");
      }
    } finally {
      googleLock.current = false;
      setGooglePending(false);
    }
  };

  const fetchUserRole = async (userId) => {
    try {
      const docRef = doc(db, "users", userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data().role;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      return null;
    }
  };

  const createNewUserWithRole = async (userId, userEmail) => {
    try {
      await setDoc(doc(db, "users", userId), {
        email: userEmail,
        role: "user",
      });
    } catch (error) {
      console.error("Error creating new user:", error);
    }
  };

  const navigateTo = (path) => {
    navigate(path);
  };

  return (
    <div className="LI-login-container">
      <h1 className="LI-login-header-title">The iSpeakWell Project</h1>
      <h5 className="LI-tagline">Become a member of the iSpeakWell crew.</h5>
      <h3>Practice Speaking with Real Humans & AI.</h3>

      {showLoginChoices && (
        <>
          <form className="LI-Login-Input" onSubmit={handleLogin}>
            <input
              className="LI-input-email"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loginPending}
              autoComplete="email"
            />
            <input
              className="LI-input-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loginPending}
              autoComplete="current-password"
              aria-describedby={error ? "login-error" : undefined}
              aria-invalid={error ? "true" : undefined}
            />
            <label style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", color: "var(--isu-text-dim)", marginTop: "8px" }}>
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                style={{ marginRight: "6px" }}
                disabled={loginPending}
              />
              Remember this device
            </label>
            {rememberDevice && (
              <p style={{ fontSize: "0.85rem", color: "var(--isu-text-dim)", marginTop: "4px" }}>
                ✅ We won’t ask for a code again on this device for 30 days.
              </p>
            )}
            <div className="LI-button-container">
              <button className="LI-login-button" type="submit" disabled={loginPending}>
                {loginPending ? "Signing in…" : "Login"}
              </button>
            </div>
          </form>

          {error && <p id="login-error" className="LI-error-message" role="alert">{error}</p>}
          {info && <p className="LI-info-message">{info}</p>}

          {unverifiedUser && (
            <button onClick={handleResendVerification} className="LI-resend-button" type="button">
              Resend Verification Email
            </button>
          )}

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="LI-google-sign-in-button"
            disabled={loginPending}
          >
            <img src={googleLogo} alt="Google sign-in" />
            Sign in with Google
          </button>

          <h3 className="LI-noAccount">
            Don't have an account? <Link to="/signup" className="LI-signup-link">Sign up</Link>
          </h3>
          <h3 className="LI-forgotPWD">
            Forgot your password? <Link to="/password-reset" className="LI-pwReset-link">Reset it here</Link>
          </h3>
        </>
      )}

      {googlePending && !mfaFactor && (
        <p className="LI-auth-pending" role="status">Signing in with Google…</p>
      )}

      {mfaFactor === "sms" && !loginVerificationId && (
        <p className="LI-auth-pending" role="status">Sending a verification code to your phone…</p>
      )}

      {mfaFieldReady && (
        <form
          className="LI-mfa-section"
          onSubmit={mfaFactor === "sms" ? handleVerifySms : handleVerifyTotp}
        >
          <h4 id="mfa-title">
            {mfaFactor === "sms"
              ? "Enter your text message code"
              : mfaFactor === "other"
                ? "Enter your verification code"
                : "Enter your authenticator code"}
          </h4>
          <p id="mfa-hint" className="LI-helper-text">
            {mfaFactor === "sms"
              ? "Enter the code sent to your phone."
              : mfaFactor === "other"
                ? "Enter your second-factor code."
                : "Enter the 6-digit code from your authenticator app."}
          </p>
          <input
            ref={mfaInputRef}
            id="mfa-code"
            className="LI-input-sms"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            autoFocus
            aria-labelledby="mfa-title"
            aria-describedby={error ? "mfa-hint mfa-error" : "mfa-hint"}
            aria-invalid={error ? "true" : undefined}
            value={mfaFactor === "sms" ? smsCode : totpCodeLogin}
            onChange={(e) => {
              const next = e.target.value.replace(/\D/g, "").slice(0, 6);
              if (mfaFactor === "sms") setSmsCode(next);
              else setTotpCodeLogin(next);
              if (error) setError("");
            }}
            disabled={verifyingMfa}
          />
          {error && (
            <p id="mfa-error" className="LI-error-message" role="alert">{error}</p>
          )}
          <div className="LI-button-container">
            <button className="LI-login-button" type="submit" disabled={verifyingMfa}>
              Verify Code
            </button>
          </div>
        </form>
      )}

      <div className="links-section">
        <button className="link-button" onClick={() => navigateTo("/about")}>About</button>
        <button className="link-button" onClick={() => navigateTo("/careers")}>Careers</button>
        <button className="link-button" onClick={() => navigateTo("/tokens")}>Tokens</button>
        <button className="link-button" onClick={() => navigateTo("/contactus")}>Contact Us</button>
        <button className="link-button" onClick={() => navigateTo("/login")}>Login</button>
      </div>

      <div id="recaptcha-login-container" style={{ display: "none" }} />
    </div>
  );
}

export default Login;